import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type FormEvent,
} from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  X,
  UserRoundPen,
  BellRing,
  CircleCheck,
  CalendarClock,
  QrCode,
  CircleX,
  ChefHat,
  Ellipsis,
  Banknote,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Effect, Schema } from "effect";
import { useAuth } from "#auth.tsx";
import { ApiError, Credential } from "#api.ts";
import { runEffectMutation } from "#effect-query.ts";
import {
  formatMoney,
  formatTime,
  Order,
  orderMutationSchema,
  type OrderState,
} from "#orders.ts";
import { useOrders } from "#orders-context.tsx";
import {
  KitchenNotesEditor,
  LineKitchenNotes,
  OrderInformationEditor,
  OrderKitchenNotes,
} from "#kitchen-notes.tsx";
import type { KitchenNotesRecord } from "../../../packages/common/src/schemas/kitchen-notes.ts";
import { useLanguage } from "#i18n.tsx";
import {
  findKitchenLine,
  kitchenLineKey,
  kitchenLineLabel,
  kitchenLineLimit,
  type KitchenLine,
} from "../../../packages/common/src/schemas/kitchen-lines.ts";

/** Native modal with focus trapping and a viewport that follows the iPad keyboard. */
export function StaffDialog({
  title,
  close,
  children,
  closeLabel,
  presentation = "full",
}: {
  readonly title: string;
  readonly close: () => void;
  readonly children: ReactNode;
  readonly closeLabel?: string;
  readonly presentation?: "confirmation" | "filters" | "full" | "order";
}) {
  const { t } = useLanguage();
  const ref = useRef<HTMLDialogElement>(null);
  const pressedBackdrop = useRef(false);
  const titleId = useId();
  const presentationClass = {
    confirmation: " confirmation-dialog",
    filters: " filter-dialog",
    full: "",
    order: " order-detail-dialog",
  }[presentation];
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const previous = document.activeElement;
    const viewport = window.visualViewport;
    const update = () => {
      element.style.setProperty(
        "--visible-height",
        `${viewport?.height ?? window.innerHeight}px`,
      );
      element.style.setProperty(
        "--visible-top",
        `${viewport?.offsetTop ?? 0}px`,
      );
    };
    update();
    element.showModal();
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (previous instanceof HTMLElement)
        previous.focus({ preventScroll: true });
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={`staff-dialog${presentationClass}`}
      aria-labelledby={titleId}
      onPointerDown={(event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        pressedBackdrop.current =
          event.target === event.currentTarget &&
          (event.clientX < bounds.left ||
            event.clientX > bounds.right ||
            event.clientY < bounds.top ||
            event.clientY > bounds.bottom);
      }}
      onClick={(event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          pressedBackdrop.current &&
          event.target === event.currentTarget &&
          (event.clientX < bounds.left ||
            event.clientX > bounds.right ||
            event.clientY < bounds.top ||
            event.clientY > bounds.bottom)
        )
          close();
        pressedBackdrop.current = false;
      }}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <header className="dialog-header">
        <h2 id={titleId}>{title}</h2>
        <button
          className="icon-button"
          type="button"
          aria-label={closeLabel ?? t("common.closeDialog")}
          onClick={close}
        >
          <X />
        </button>
      </header>
      {children}
    </dialog>
  );
}

/** Shared, large-format order lines for kitchen cards and details. */
export function OrderLines({
  order,
  visibility = "customer",
  editLine,
  pending = false,
}: {
  readonly order: Order;
  readonly visibility?: "staff" | "customer";
  readonly editLine?: (line: KitchenLine) => void;
  readonly pending?: boolean;
}) {
  const { t } = useLanguage();
  if (order.unreadable)
    return (
      <p className="alert" role="alert">
        {t("orders.unreadable")}
      </p>
    );
  return (
    <ul className="order-lines">
      {order.lines.map((line, index) => {
        const contents = (
          <>
            <div>
              <strong>
                {line.kitchenName ?? line.name ?? t("orders.item")}
              </strong>
              {line.variationName && <span>{line.variationName}</span>}
              {line.note && <span className="order-note">{line.note}</span>}
              {line.modifiers?.map((modifier, i) => (
                <span className="modifier" key={i}>
                  {modifier.quantity && modifier.quantity !== "1"
                    ? `${modifier.quantity} × `
                    : ""}
                  {modifier.name}
                </span>
              ))}
              {visibility === "staff" && (
                <LineKitchenNotes
                  notes={order.notesFor(line)}
                  limit={kitchenLineLimit(line)}
                />
              )}
            </div>
            <strong className="quantity">× {line.quantity}</strong>
          </>
        );
        const editable = visibility === "staff" && editLine;
        return (
          <li key={index}>
            {editable ? (
              <button
                type="button"
                className="line-item-action"
                aria-label={t("dialog.lineInstructionsTitle", {
                  item: kitchenLineLabel(line),
                  number: index + 1,
                })}
                disabled={
                  pending || !findKitchenLine(order.lines, kitchenLineKey(line))
                }
                title={
                  findKitchenLine(order.lines, kitchenLineKey(line))
                    ? t("dialog.editItemInstructions")
                    : t("dialog.unidentifiableItem")
                }
                onClick={() => editLine(line)}
              >
                {contents}
              </button>
            ) : (
              contents
            )}
          </li>
        );
      })}
    </ul>
  );
}

const cashResponse = Schema.Struct({
  _tag: Schema.Literal("OkKioskCashPayment"),
  orderId: Schema.String,
  paymentId: Schema.String,
});

const tokenResponse = Schema.Struct({
  success: Schema.Literal(true),
  token: Schema.String.pipe(Schema.minLength(1)),
  expiresIn: Schema.transform(Schema.Literal("1h"), Schema.Number, {
    decode: () => 3600,
    encode: () => "1h" as const,
  }),
});

/** Restore order details, pickup editing, scheduling, status actions, and customer QR tracking. */
export function OrderDialog({
  order,
  close,
  mode = "staff",
}: {
  readonly order: Order;
  readonly close: () => void;
  readonly mode?: "staff" | "display";
}) {
  const { api, state } = useAuth();
  const { language, orderStatus, t } = useLanguage();
  const { merge, setNotice, recordClosure } = useOrders();
  const [panel, setPanel] = useState<
    | "details"
    | "pickup"
    | "schedule"
    | "status"
    | "cancel"
    | "information"
    | { readonly _tag: "line"; readonly line: KitchenLine }
  >("details");
  const [formError, setFormError] = useState<string>();
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  type UpdateInput = {
    readonly previous: Order;
    readonly body:
      | { pickup_id: string }
      | { scheduled_at: string }
      | { order_state: OrderState }
      | { kitchen_notes: Order["orderNotes"] }
      | {
          line_kitchen_note: KitchenNotesRecord & { readonly line_key: string };
        };
    readonly expectedRevision?: string;
  };
  const updateMutation = useMutation<
    NonNullable<typeof orderMutationSchema.Type>,
    ApiError,
    UpdateInput
  >({
    retry: false,
    mutationFn: ({ previous, body, expectedRevision }: UpdateInput) =>
      runEffectMutation(
        api
          .requestEffect({
            path: `/merchants/${encodeURIComponent(previous.merchantId)}/orders/${encodeURIComponent(previous.id)}`,
            method: "PUT",
            body: {
              ...body,
              ...("order_state" in body ||
              "kitchen_notes" in body ||
              "line_kitchen_note" in body
                ? {
                    expected_revision:
                      expectedRevision ?? previous.revision ?? "unversioned",
                  }
                : {}),
            },
            schema: orderMutationSchema,
          })
          .pipe(
            Effect.flatMap((result) =>
              result
                ? Effect.succeed(result)
                : Effect.fail(new ApiError(404, t("dialog.orderNotFound"))),
            ),
          ),
      ),
    onSuccess: (result, { previous, body }) => {
      merge([result.order]);
      if ("order_state" in body) recordClosure(previous, result.order);
      setNotice(
        result.liveUpdatesDelayed ? t("dialog.saveDelayed") : undefined,
      );
      if ("line_kitchen_note" in body || "kitchen_notes" in body)
        setPanel("details");
      else close();
    },
  });
  const update = (body: UpdateInput["body"], expectedRevision?: string) => {
    setFormError(undefined);
    updateMutation.reset();
    updateMutation.mutate({ previous: order, body, expectedRevision });
  };
  const savePickup = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("pickupName");
    if (typeof name !== "string" || !name.trim()) {
      setFormError(t("dialog.pickupRequired"));
      return;
    }
    update({ pickup_id: name.trim() });
  };
  const saveSchedule = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("scheduledAt");
    if (typeof value !== "string") return;
    const selected = new Date(value);
    if (
      !Number.isFinite(selected.getTime()) ||
      selected.getTime() <= Date.now()
    ) {
      setFormError(t("dialog.futureTimeRequired"));
      return;
    }
    update({ scheduled_at: selected.toISOString() });
  };
  const tokenMutation = useMutation<typeof tokenResponse.Type, ApiError, void>({
    retry: false,
    mutationFn: () =>
      runEffectMutation(
        api.requestEffect({
          path: "/auth/order-token",
          method: "POST",
          body: { orderId: order.squareId, merchantId: order.merchantId },
          schema: tokenResponse,
        }),
      ),
  });
  const cashMutation = useMutation<typeof cashResponse.Type, ApiError, void>({
    retry: false,
    mutationFn: () =>
      runEffectMutation(
        api.requestEffect({
          path: `/kiosk/orders/${encodeURIComponent(order.squareId)}/cash-payment`,
          method: "POST",
          schema: cashResponse,
        }),
      ),
    onSuccess: () => setNotice(t("dialog.cashRecorded")),
  });
  const generateQR = () => {
    setFormError(undefined);
    tokenMutation.reset();
    tokenMutation.mutate();
  };
  const back = () => {
    setPanel("details");
    setFormError(undefined);
    updateMutation.reset();
    tokenMutation.reset();
  };
  const pending = updateMutation.isPending || tokenMutation.isPending;
  const error =
    formError ??
    updateMutation.error?.message ??
    tokenMutation.error?.message ??
    cashMutation.error?.message;
  const tracking = tokenMutation.data
    ? {
        credential: new Credential(tokenMutation.data.token),
        expiresAt:
          tokenMutation.submittedAt + tokenMutation.data.expiresIn * 1000,
      }
    : undefined;
  const footer = (label: string) => (
    <footer className="dialog-footer">
      <button
        className="secondary"
        type="button"
        onClick={back}
        disabled={pending}
      >
        {t("common.back")}
      </button>
      <button className="primary" type="submit" disabled={pending}>
        {pending ? t("dialog.saving") : label}
      </button>
    </footer>
  );
  const trackingControl =
    tracking && tracking.expiresAt > clock ? (
      <div className="tracking-qr">
        <QRCodeSVG
          size={192}
          value={`${window.location.origin}/orders/${encodeURIComponent(order.squareId)}#token=${encodeURIComponent(tracking.credential.reveal())}`}
        />
        <p>
          {t("dialog.trackingExpires", {
            time: formatTime(
              new Date(tracking.expiresAt).toISOString(),
              false,
              language,
            ),
          })}
        </p>
      </div>
    ) : (
      <button
        className="secondary action-tile"
        aria-label={t("dialog.generateQr")}
        disabled={pending}
        onClick={() => void generateQR()}
      >
        <QrCode aria-hidden="true" />
        {pending ? t("dialog.generating") : t("actions.qr")}
      </button>
    );
  return (
    <StaffDialog
      title={
        typeof panel === "object"
          ? t("dialog.instructionsTitle", {
              item: kitchenLineLabel(panel.line),
            })
          : panel === "pickup"
            ? t("dialog.pickup")
            : panel === "information"
              ? t("dialog.orderInformationTitle", { label: order.label })
              : panel === "schedule"
                ? t("dialog.schedule")
                : t("dialog.orderTitle", { label: order.label })
      }
      closeLabel={t("dialog.closeOrder")}
      close={pending ? () => undefined : close}
      presentation={panel === "details" && mode === "staff" ? "order" : "full"}
    >
      {typeof panel === "object" ? (
        <KitchenNotesEditor
          order={order}
          line={panel.line}
          pending={pending}
          error={error}
          back={back}
          save={(notes, revision) =>
            update(
              {
                line_kitchen_note: {
                  ...notes.toRecord(),
                  line_key: kitchenLineKey(panel.line),
                },
              },
              revision,
            )
          }
        />
      ) : panel === "information" ? (
        <OrderInformationEditor
          order={order}
          pending={pending}
          error={error}
          back={() => {
            setFormError(undefined);
            updateMutation.reset();
            setPanel("details");
          }}
          save={(notes, revision) => update({ kitchen_notes: notes }, revision)}
        />
      ) : panel === "pickup" ? (
        <form className="dialog-form" onSubmit={savePickup}>
          <div className="dialog-body">
            <p>{t("dialog.pickupHelp")}</p>
            {error && (
              <p role="alert" className="alert">
                {error}
              </p>
            )}
            <label className="field">
              {t("dialog.pickup")}
              <input
                name="pickupName"
                autoComplete="off"
                enterKeyHint="done"
                defaultValue={order.pickupName}
                maxLength={100}
                required
                autoFocus
              />
            </label>
          </div>
          {footer(t("dialog.savePickup"))}
        </form>
      ) : panel === "schedule" ? (
        <form className="dialog-form" onSubmit={saveSchedule}>
          <div className="dialog-body">
            <p>{t("dialog.scheduleHelp")}</p>
            {error && (
              <p role="alert" className="alert">
                {error}
              </p>
            )}
            <label className="field">
              {t("dialog.preparationTime")}
              <input type="datetime-local" name="scheduledAt" required />
            </label>
            {order.scheduledAt && (
              <button
                className="secondary"
                type="button"
                disabled={pending}
                onClick={() => update({ scheduled_at: "" })}
              >
                {t("dialog.clearSchedule")}
              </button>
            )}
          </div>
          {footer(t("dialog.saveSchedule"))}
        </form>
      ) : (
        <>
          <div className="dialog-body">
            {error && (
              <p role="alert" className="alert">
                {error}
              </p>
            )}
            {panel === "cancel" ? (
              <>
                <p>{t("dialog.cancelPrompt")}</p>
                <p className="muted">{t("dialog.cancelWarning")}</p>
              </>
            ) : panel === "status" ? (
              <div className="action-list action-tiles">
                <button
                  className="secondary action-tile"
                  aria-label={
                    order.scheduledAt
                      ? t("dialog.changeSchedule")
                      : t("dialog.schedule")
                  }
                  disabled={pending}
                  onClick={() => setPanel("schedule")}
                >
                  <CalendarClock aria-hidden="true" />
                  {t("actions.schedule")}
                </button>
                {state.kind === "authenticated" &&
                  state.role === "admin" &&
                  trackingControl}
                {(
                  [
                    "order_paid_and_in_progress",
                    "order_ready",
                    "order_completed",
                  ] as const
                )
                  .filter(
                    (status) =>
                      status !== order.state &&
                      (status === "order_paid_and_in_progress" ||
                        !order.active),
                  )
                  .map((status) => (
                    <button
                      key={status}
                      className="secondary action-tile"
                      disabled={pending}
                      onClick={() => update({ order_state: status })}
                    >
                      {status === "order_paid_and_in_progress" ? (
                        <ChefHat aria-hidden="true" />
                      ) : status === "order_ready" ? (
                        <BellRing aria-hidden="true" />
                      ) : (
                        <CircleCheck aria-hidden="true" />
                      )}
                      {status === "order_paid_and_in_progress"
                        ? t("dialog.moveInProgress")
                        : t("dialog.markStatus", {
                            status:
                              orderStatus(status).toLocaleLowerCase(language),
                          })}
                    </button>
                  ))}
                {order.state !== "order_canceled" && (
                  <button
                    className="danger secondary action-tile"
                    aria-label={t("dialog.cancelAction")}
                    disabled={pending}
                    onClick={() => setPanel("cancel")}
                  >
                    <CircleX aria-hidden="true" />
                    {t("actions.cancel")}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="card-row">
                  <span className={`pill ${order.state}`}>
                    {orderStatus(order.state)}
                  </span>
                  <span className="muted">
                    {formatTime(order.createdAt, true, language)}
                  </span>
                </div>
                <p className="order-reference">
                  {t("dialog.squareOrder", { id: order.squareId })}
                </p>
                {order.scheduledAt && (
                  <p className="schedule-label">
                    {t("dialog.scheduledFor", {
                      time: formatTime(order.scheduledAt, true, language),
                    })}
                  </p>
                )}
                {mode === "staff" && (
                  <>
                    <OrderKitchenNotes order={order} />
                    <button
                      type="button"
                      className="secondary"
                      disabled={pending}
                      onClick={() => setPanel("information")}
                    >
                      {t("notes.editOrder")}
                    </button>
                  </>
                )}
                <OrderLines
                  order={order}
                  visibility={mode === "staff" ? "staff" : "customer"}
                  pending={pending}
                  editLine={
                    mode === "staff"
                      ? (line) => setPanel({ _tag: "line", line })
                      : undefined
                  }
                />
                <div className="order-total">
                  <strong>{t("orders.total")}</strong>
                  <strong>{formatMoney(order.total)}</strong>
                </div>
                {mode === "display" &&
                  state.kind === "authenticated" &&
                  state.role === "admin" &&
                  trackingControl}
              </>
            )}
          </div>
          <footer
            className={`dialog-footer ${panel === "details" ? "order-primary-actions" : ""}`}
          >
            {panel !== "details" && (
              <button className="secondary" disabled={pending} onClick={back}>
                {t("common.back")}
              </button>
            )}
            {panel === "details" && mode === "staff" && (
              <button
                className="secondary action-tile pickup-action"
                aria-label={t("dialog.setPickup")}
                disabled={pending}
                onClick={() => setPanel("pickup")}
              >
                <UserRoundPen aria-hidden="true" />
                {t("actions.pickup")}
              </button>
            )}
            {panel === "details" &&
              mode === "staff" &&
              order.awaitsCounterPayment &&
              !cashMutation.isSuccess && (
                <button
                  className="primary action-tile cash-action"
                  aria-label={t("dialog.recordCash")}
                  disabled={pending || cashMutation.isPending}
                  onClick={() => cashMutation.mutate()}
                >
                  <Banknote aria-hidden="true" />
                  {t("actions.cashReceived")}
                </button>
              )}
            {panel === "details" &&
              mode === "staff" &&
              order.active &&
              order.state !== "order_ready" && (
                <button
                  className="secondary action-tile ready-action"
                  aria-label={t("dialog.markStatus", {
                    status:
                      orderStatus("order_ready").toLocaleLowerCase(language),
                  })}
                  disabled={pending}
                  onClick={() => update({ order_state: "order_ready" })}
                >
                  <BellRing aria-hidden="true" />
                  {t("actions.ready")}
                </button>
              )}
            {panel === "details" && mode === "staff" && order.active && (
              <button
                className="primary action-tile complete-action"
                aria-label={t("dialog.completeOrder")}
                disabled={pending}
                onClick={() => update({ order_state: "order_completed" })}
              >
                <CircleCheck aria-hidden="true" />
                {pending ? t("dialog.completing") : t("actions.complete")}
              </button>
            )}
            {panel === "details" && mode === "staff" && (
              <button
                className="secondary more-order-options action-tile"
                aria-label={t("dialog.moreOptions")}
                disabled={pending}
                onClick={() => setPanel("status")}
              >
                <Ellipsis aria-hidden="true" />
                {t("actions.more")}
              </button>
            )}
            {panel === "cancel" && (
              <button
                className="primary danger"
                disabled={pending}
                onClick={() => update({ order_state: "order_canceled" })}
              >
                {pending ? t("dialog.cancelling") : t("dialog.cancelOrder")}
              </button>
            )}
          </footer>
        </>
      )}
    </StaffDialog>
  );
}
